import { makeAutoObservable, runInAction } from 'mobx'
import { AxiosError } from 'axios'
import { ReturnsLoadingType, ReturnsFilters } from './types'
import { ReturnsServices } from '~/shared/api/services/returns'
import {
  ProductVariationResponse,
  ProductVariationByBarcodeResponse,
  Return,
  ReturnPayloadType,
  SearchOrder,
  SearchOrdersParams,
} from '~/shared/api/services/returns/types'
import { handleAxiosError } from '~/shared/lib'
import { SelectOption } from '~/shared/ui'
import { OrdersService } from '~/shared/api/services/orders'

interface SearchParams {
  productVariationIds: string[]
  orderNumber: string
  customerName: string
  shippingMethod: { label: string; value: string } | null
  number: string
}

export interface SelectedOptions {
  [key: string]: SelectOption | null
}

export interface ProductAdditionFormValues {
  selectedStore: SelectOption | null
  selectedProduct: SelectOption | null
  selectedOptions: SelectedOptions
}

const INITIAL_FORM_VALUES = {
  selectedStore: null as SelectOption | null,
  selectedProduct: null as SelectOption | null,
  selectedOptions: {} as Record<string, SelectOption>,
}

const INITIAL_SEARCH_PARAMS: SearchParams = {
  productVariationIds: [],
  orderNumber: '',
  customerName: '',
  shippingMethod: null,
  number: '',
}

export class ReturnsStore {
  returns: Return[] = []

  loaders: ReturnsLoadingType = {
    returns: false,
    loadMoreReturns: false,
    createReturn: false,
    returnById: false,
    addingProduct: false,
  }

  searchOrdersLoader = false

  searchOrdersError: { status: number | null; message: string } | null = null

  private orderSearchRequestId = 0

  formData = {
    comment: null,
  }

  returnById: Return = {
    id: '',
    number: null,
    state: '',
    products: {
      missing: { active: false, products: [] },
      ruined: { active: false, products: [] },
      returned: { active: false, products: [] },
    },
    date: '',
    orderId: null,
    comment: null,
    orderNumber: null,
    storeName: null,
    storeId: null,
    customerName: '',
    customerEmail: '',
    paymentMethod: '',
    shippingMethod: '',
  }

  requestSettings = {
    limit: 20,
    offset: 0,
    isAllLoaded: false,
  }

  searchParams: SearchParams = INITIAL_SEARCH_PARAMS

  selectedProducts: ProductVariationResponse[] = []

  ordersList: SearchOrder[] = []

  selectedOrderId: string | null = null

  formValues: ProductAdditionFormValues = INITIAL_FORM_VALUES

  constructor() {
    makeAutoObservable(this)
  }

  activeLoader = (key: keyof ReturnsLoadingType) => {
    this.loaders = { ...this.loaders, [key]: true }
  }

  closeLoader = (key: keyof ReturnsLoadingType) => {
    this.loaders = { ...this.loaders, [key]: false }
  }

  setFormValues = (data: Partial<ProductAdditionFormValues>) => {
    this.formValues = { ...this.formValues, ...data }
  }

  resetFormValues = () => {
    this.formValues = INITIAL_FORM_VALUES
  }

  resetSearchParams = () => {
    this.searchParams = INITIAL_SEARCH_PARAMS
  }

  clearSelectedOrder = () => {
    this.selectedOrderId = null
  }

  invalidateOrderSearch = () => {
    this.orderSearchRequestId += 1
    this.ordersList = []
    this.searchOrdersError = null
    this.searchOrdersLoader = false
    this.clearSelectedOrder()
  }

  clearFormInput = () => {
    this.formData = {
      comment: null,
    }

    this.selectedProducts = []
    this.invalidateOrderSearch()
  }

  resetCreateReturnState = () => {
    this.clearFormInput()
    this.resetFormValues()
    this.resetSearchParams()
  }

  setFormReturnData = (key: keyof Return, value: Return[keyof Return]) => {
    this.formData = { ...this.formData, [key]: value }
  }

  handleOrderSelect = (orderId: string) => {
    if (orderId === this.selectedOrderId) {
      this.clearSelectedOrder()
    } else {
      this.selectedOrderId = orderId
    }
  }

  getReturns = async (options?: {
    action?: 'loadMoreReturns'
    filters?: ReturnsFilters
  }) => {
    const { action, filters } = options || {}
    this.activeLoader(action || 'returns')

    try {
      const params = {
        ...filters,
        limit: this.requestSettings.limit,
        offset: action === 'loadMoreReturns' ? this.requestSettings.offset : 0,
      }

      const { data } = await ReturnsServices.getReturns({
        params,
      })

      if (action === 'loadMoreReturns') {
        runInAction(() => {
          this.returns = [...this.returns, ...data]
        })
      } else {
        runInAction(() => {
          this.returns = data
        })
      }
      runInAction(() => {
        this.requestSettings = {
          ...this.requestSettings,
          isAllLoaded: data.length < this.requestSettings.limit,
          offset:
            action === 'loadMoreReturns'
              ? this.requestSettings.offset + this.requestSettings.limit
              : 20,
        }
      })

      return data
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      this.closeLoader(action || 'returns')
    }
  }

  createReturn = async (data: ReturnPayloadType) => {
    try {
      this.activeLoader('createReturn')

      await ReturnsServices.createReturn(data)
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      this.closeLoader('createReturn')
    }
  }

  getReturnById = async (id: string) => {
    try {
      this.activeLoader('returnById')

      const { data } = await ReturnsServices.getReturnById(id)

      runInAction(() => {
        this.returnById = data
      })
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      this.closeLoader('returnById')
    }
  }

  get productVariationIds() {
    return [...new Set(this.selectedProducts.map((product) => product.id))]
  }

  getProductVariant = async (
    optionId: string,
    storeId: string,
    selectedOptions?: { [key: string]: SelectOption | null },
  ) => {
    this.activeLoader('addingProduct')
    try {
      const params =
        selectedOptions && Object.keys(selectedOptions).length > 0
          ? {
              optionValues: Object.values(selectedOptions).map((e) =>
                e ? e.value : '',
              ),
            }
          : null

      const { data } = await OrdersService.getProductVariation(optionId, {
        params,
      })

      runInAction(() => {
        this.selectedProducts = [...this.selectedProducts, { ...data, storeId }]
        this.invalidateOrderSearch()
      })
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.error || error.message)
      }
      throw error
    } finally {
      this.closeLoader('addingProduct')
    }
  }

  getProductVariantByBarcode = async (barcode: string, storeId: string) => {
    this.activeLoader('addingProduct')
    try {
      const { data } =
        await ReturnsServices.getProductVariationByBarcode(barcode)

      runInAction(() => {
        this.selectedProducts = [
          ...this.selectedProducts,
          ReturnsStore.mapProductVariationByBarcode(data, storeId),
        ]
        this.invalidateOrderSearch()
      })
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      this.closeLoader('addingProduct')
    }
  }

  private static mapProductVariationByBarcode = (
    product: ProductVariationByBarcodeResponse,
    storeId: string,
  ): ProductVariationResponse => ({
    id: product.id,
    product_name: product.product_name,
    image: product.image ?? null,
    options: product.options_text
      .split('<br>')
      .filter(Boolean)
      .map((option) => {
        const [optionName, ...optionValue] = option.split(': ')

        return {
          optionName,
          optionValue: optionValue.join(': '),
        }
      }),
    storeId,
  })

  removeProductVariant = (index: number) => {
    runInAction(() => {
      this.selectedProducts = this.selectedProducts.filter(
        (_, i) => i !== index,
      )
      this.invalidateOrderSearch()
    })
  }

  updateSearchParam = <K extends keyof typeof this.searchParams>(
    key: K,
    value: (typeof this.searchParams)[K],
  ) => {
    this.searchParams[key] = value
    this.invalidateOrderSearch()
  }

  searchOrders = async () => {
    this.orderSearchRequestId += 1
    const requestId = this.orderSearchRequestId
    const { productVariationIds } = this

    this.searchOrdersLoader = true
    this.searchOrdersError = null
    this.ordersList = []
    this.clearSelectedOrder()

    try {
      const params: SearchOrdersParams = {}

      if (productVariationIds.length > 0) {
        params.productVariationIds = productVariationIds
      }

      if (this.searchParams.orderNumber) {
        params.orderNumber = this.searchParams.orderNumber
      }

      if (this.searchParams.customerName) {
        params.customerName = this.searchParams.customerName
      }

      if (
        this.searchParams.shippingMethod &&
        this.searchParams.shippingMethod.value
      ) {
        params.shippingMethod = this.searchParams.shippingMethod.value
      }

      const { data } = await ReturnsServices.searchOrdersWithFilters(params)

      if (requestId !== this.orderSearchRequestId) return

      runInAction(() => {
        this.ordersList = [...data.orders]
      })

      return data
    } catch (error) {
      if (requestId !== this.orderSearchRequestId) return

      const status =
        error instanceof AxiosError ? (error.response?.status ?? null) : null
      const responseError =
        error instanceof AxiosError
          ? error.response?.data?.error || error.response?.data?.message
          : null
      const message =
        (typeof responseError === 'string' && responseError) ||
        (error instanceof Error ? error.message : '')

      runInAction(() => {
        this.ordersList = []
        this.searchOrdersError = { status, message }
      })
    } finally {
      if (requestId === this.orderSearchRequestId) {
        runInAction(() => {
          this.searchOrdersLoader = false
        })
      }
    }
  }

  defaultOrderList = () => {
    this.ordersList = []
    this.clearSelectedOrder()
  }

  updateProductStatus = (index: number, newStatus: string) => {
    this.selectedProducts = this.selectedProducts.map((product, i) => {
      return i === index ? { ...product, state: newStatus } : product
    })
  }
}
